/**
 * Dispara un evento de analítica UNA sola vez por montaje del componente.
 *
 * Existe porque las props del evento se arman en cada render (objeto nuevo), lo
 * que haría re-disparar el efecto con cada cambio de estado de la página — y una
 * vista de página contada diez veces arruina cualquier tasa de conversión, que
 * es justo para lo que sirve `pagina_vista`.
 *
 * El `ref` lo resuelve y permite dejar las dependencias completas para no pelear
 * con `react-hooks/exhaustive-deps`.
 *
 * Úsalo solo para eventos de "se abrió/se vio algo". Las acciones con resultado
 * (agendar, separar, cotizar) se trackean en su handler, no al montar.
 *
 * Si el evento depende de datos que llegan por fetch (p. ej. la ficha de un
 * vehículo), pásale `listo: false` hasta que estén: el hook espera y dispara en
 * cuanto se cumple, una sola vez.
 */
import { useEffect, useRef } from "react";
import { trackEvent, type AnalyticsEventName, type AnalyticsEvents } from "../../../lib/analytics";

export function useTrackOnce<K extends AnalyticsEventName>(name: K, props: AnalyticsEvents[K], listo: boolean = true): void {
    const yaEnviado = useRef(false);

    useEffect(() => {
        if (yaEnviado.current || !listo) return;
        yaEnviado.current = true;
        trackEvent(name, props);
        // `props` cambia de identidad en cada render a propósito: el ref de arriba
        // es lo que garantiza el disparo único, no la lista de dependencias.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, listo]);
}
